import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IProduct } from 'src/redux/products/productsSlice';
import { addToCartAPI, getCartAPI, removeFromCartAPI } from 'src/constant/api/userAPI';
import Cookies from 'js-cookie';

export interface ICartItems {
    items: IProduct[];
    totalPrice: number;
    loading: boolean;
}

const initialState: ICartItems = { items: [], totalPrice: 0, loading: false };

const totalPriceUpdating = (items: IProduct[]) => {
    return items.reduce((total, item) => {
        return total + item.price * item.quantity;
    }, 0);
};

// Async thunks for backend cart operations
export const fetchCart = createAsyncThunk('cart/fetchCart', async (_, { rejectWithValue }) => {
    try {
        const token = Cookies.get('accessToken');
        if (!token) return { items: [] };
        const { data } = await getCartAPI(token);

        if (!data.items || data.items.length === 0) return { items: [] };

        // image_dir now contains Cloudinary URLs directly from the API
        return { items: data.items };
    } catch (err: any) {
        return rejectWithValue(err.response?.data?.message || 'Failed to fetch cart');
    }
});

export const addToBackendCart = createAsyncThunk('cart/addToBackend', async (productId: string, { rejectWithValue }) => {
    try {
        const token = Cookies.get('accessToken');
        if (!token) return null;
        const { data } = await addToCartAPI(token, productId);
        return data;
    } catch (err: any) {
        return rejectWithValue(err.response?.data?.message || 'Failed to add to cart');
    }
});

export const removeFromBackendCart = createAsyncThunk(
    'cart/removeFromBackend',
    async ({ productId, removeAll }: { productId: string; removeAll: boolean }, { rejectWithValue }) => {
        try {
            const token = Cookies.get('accessToken');
            if (!token) return null;
            const { data } = await removeFromCartAPI(token, productId, removeAll);
            return data;
        } catch (err: any) {
            return rejectWithValue(err.response?.data?.message || 'Failed to remove from cart');
        }
    }
);

export const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        resetCartItems: (state) => {
            state.items = [];
            state.totalPrice = 0;
        },
        addItem: (state, action) => {
            const newItem = action.payload;
            const existingItem = state.items.find((item) => item.id === newItem.id);
            if (existingItem) {
                existingItem.quantity += 1;
            } else {
                state.items.push({ ...newItem, quantity: 1 });
            }
            state.totalPrice = totalPriceUpdating(state.items);
        },
        removeItem: (state, action) => {
            const productId = action.payload;
            state.items = state.items.filter((item) => item.id !== productId);
            state.totalPrice = totalPriceUpdating(state.items);
        },
        updateItemQuantity: (state, action) => {
            const { product, quantity } = action.payload;
            const itemToUpdate = state.items.find((item) => item.id === product.id);
            if (itemToUpdate) {
                itemToUpdate.quantity += quantity;
            } else {
                state.items.push({ ...product, quantity: quantity });
            }
            state.totalPrice = totalPriceUpdating(state.items);
        },
        increaseItemQuantity: (state, action) => {
            const productId = action.payload;
            const itemIncrease = state.items.find((item) => item.id === productId);

            if (itemIncrease) {
                itemIncrease.quantity += 1;
            }
            state.totalPrice = totalPriceUpdating(state.items);
        },
        decreaseItemQuantity: (state, action) => {
            const productId = action.payload;
            const itemDecrease = state.items.find((item) => item.id === productId);
            if (itemDecrease && itemDecrease.quantity > 1) {
                itemDecrease.quantity -= 1;
            } else if (itemDecrease && itemDecrease.quantity === 1) {
                state.items = state.items.filter((item) => item.id !== productId);
            }
            state.totalPrice = totalPriceUpdating(state.items);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCart.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCart.fulfilled, (state, { payload }) => {
                state.loading = false;
                if (payload.items && payload.items.length > 0) {
                    state.items = payload.items.map((item: any) => ({
                        id: item.id,
                        name: item.name,
                        category: item.category,
                        price: Number(item.price),
                        image_dir: item.image_dir,
                        description: item.description,
                        quantity: item.quantity,
                        status: item.status || 'active',
                        createdAt: item.createdAt || '',
                        updatedAt: item.updatedAt || '',
                    }));
                    state.totalPrice = totalPriceUpdating(state.items);
                }
            })
            .addCase(fetchCart.rejected, (state) => {
                state.loading = false;
            });
    },
});

export const { resetCartItems, addItem, removeItem, increaseItemQuantity, decreaseItemQuantity, updateItemQuantity } =
    cartSlice.actions;
